import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { notNull } from '@app/shared';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { ModalAuditSelectors } from '../../store';
import { AttemptStudentData, ModalAuditState } from '../../store/modal-audit.state';

enum FileTypeEnum {
  PDF,
  Image,
}

interface DataViewer {
  url: string;
  type: FileTypeEnum;
  page: number;
}

@Component({
  selector: 'app-response-card-image',
  templateUrl: './response-card-image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponseCardImageComponent implements OnDestroy {
  public dataViewer$ = new BehaviorSubject<DataViewer>(undefined);
  public zoom$ = new BehaviorSubject<number>(1);
  public loadingImg$ = new BehaviorSubject<boolean>(false);

  @ViewChild('pinchZoom') public pinchZoomComponent;

  constructor(
    private _store: Store<ModalAuditState>,
    private _destroyRef: DestroyRef,
    private _cdRef: ChangeDetectorRef,
  ) {
    this._store
      .select(ModalAuditSelectors.selectStudentActive)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged(isEqual))
      .subscribe(this.setDataViewer);
  }

  public get isZoomedIn(): boolean {
    if (this.dataViewer$.value.type === FileTypeEnum.Image) {
      return this.pinchZoomComponent?.isZoomedIn;
    }

    return this.zoom$.value === 2;
  }

  public get isImage(): boolean {
    return this.dataViewer$.value.type === FileTypeEnum.Image;
  }

  public ngOnDestroy(): void {
    this.dataViewer$.complete();
    this.zoom$.complete();
    this.loadingImg$.complete();
  }

  public setDataViewer = (student: AttemptStudentData) => {
    const url = !!student?.tentativa_response_card_upload
      ? student?.tentativa_file_url || student.tentativa_response_card_upload?.file_url
      : undefined;

    this.loadingImg$.next(!!url && url !== this.dataViewer$.value?.url);
    this.dataViewer$.next(
      url
        ? {
            url,
            type: url?.endsWith('.pdf') ? FileTypeEnum.PDF : FileTypeEnum.Image,
            page: student?.tentativa_response_card_upload_page || 1,
          }
        : undefined,
    );
  };

  public onLoadedImg() {
    this.loadingImg$.next(false);
    this._cdRef.detectChanges();
  }

  public onToggleZoom() {
    if (this.dataViewer$.value.type === FileTypeEnum.Image) {
      this.pinchZoomComponent.toggleZoom();
    } else {
      this.zoom$.next(this.zoom$.value === 1 ? 2 : 1);
    }
  }
}
